import { styled, InputAdornment, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormTrigger,
  UseFormWatch,
  RegisterOptions,
  FieldPath
} from "react-hook-form";
import { BaseButton } from "components/buttons/BaseButton";
import { useKeyPress } from "../../../../../hooks/useKeyPress";
import colors from "../../../../../theme/themeConstants";
import MarkOkIcon from "../../../../icons/Components/MarkOkIcon";
import CustomInput from "../../../CustomInput";
import { ISignUpFieldValues } from "../AuthSignUp";
import { ReactComponent as EyeIcon } from "components/icons/grey-eye.svg";
import { ReactComponent as CrossEyeIcon } from "components/icons/cross-grey-eye.svg";

interface IProps {
  next: () => void;
  control: Control<ISignUpFieldValues>;
  trigger: UseFormTrigger<ISignUpFieldValues>;
  watch: UseFormWatch<ISignUpFieldValues>;
  getValues: UseFormGetValues<ISignUpFieldValues>;
}

export const MIN_PASS_LENGTH = 8;
export const MAX_PASS_LENGTH = 20;
// https://owasp.org/www-community/OWASP_Validation_Regex_Repository
const passwordRegexp = new RegExp(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,20}$/);

const SignUpPasswordStep = (props: IProps) => {
  const { next, control, trigger, watch, getValues } = props;

  const [passLengthIsOk, setPassLengthIsOk] = useState(false);
  const [passSymbolsIsOk, setPassSymbolsIsOk] = useState(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  const enterPressed = useKeyPress("Enter");

  const handleNext = async () => {
    if (await trigger("password") && await trigger("password_confirm")) {
      next();
    }
  };

  const validate = (val: string) => {
    if (!val || val.length < MIN_PASS_LENGTH || val.length > MAX_PASS_LENGTH) {
      setPassLengthIsOk(false);
    } else {
      setPassLengthIsOk(true);
    }

    if (passwordRegexp.test(val)) {
      setPassSymbolsIsOk(true);
    } else {
      setPassSymbolsIsOk(false);
    }
  };

  useEffect(() => {
    validate(getValues().password);
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (enterPressed) {
      handleNext();
    }
    // eslint-disable-next-line
  }, [enterPressed])

  return (
    <Container>
      <Controller
        name="password"
        control={control}
        defaultValue=""
        rules={{
          required: {
            value: true,
          },
          minLength: {
            value: MIN_PASS_LENGTH,
          },
          maxLength: {
            value: MAX_PASS_LENGTH,
          },
          pattern: {
            value: passwordRegexp,
          },
        } as RegisterOptions }
        render={({ field, fieldState }) => (
          <Block>
            <label>Password</label>
            <CustomInput
              value={field.value}
              type={showPassword ? "text" : "password"}
              onChange={async (e) => {
                field.onChange(e);
                validate(e.target.value);
                await trigger("password");
              }}
              error={!!fieldState.error}
              helperText={fieldState.error ? fieldState.error.message : ""}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(prev => !prev)}
                      edge="end"
                    >
                      {showPassword ? <EyeIcon /> : <CrossEyeIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Block>
        )}
      />
      <FlexBlock>
        <MarkOkIcon color={passLengthIsOk ? "green" : "#FF536D"}/>
        <span>{MIN_PASS_LENGTH} to {MAX_PASS_LENGTH} characters</span>
      </FlexBlock>
      <FlexBlock>
        <MarkOkIcon color={passSymbolsIsOk ? "green" : "#FF536D"}/>
        <span>Upper case and lower case letters, numbers</span>
      </FlexBlock>
      <Controller
        name="password_confirm"
        control={control}
        defaultValue=""
        rules={{
          required: {
            value: true,
            message: "Required Field!"
          },
          validate: {
            value: (value) => {
              return value === watch<FieldPath<ISignUpFieldValues>>("password") || "Passwords do not match!";
            }
          }
        } as RegisterOptions }
        render={({ field, fieldState }) => (
          <Block>
            <label>Confirm Password</label>
            <CustomInput
              value={field.value}
              type={showConfirmPassword ? "text" : "password"}
              onChange={async (e) => {
                field.onChange(e);
                await trigger("password_confirm");
              }}
              error={!!fieldState.error}
              helperText={fieldState.error ? fieldState.error.message : ""}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword(prev => !prev)}
                      edge="end"
                    >
                      {showConfirmPassword ? <EyeIcon /> : <CrossEyeIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Block>
        )}
      />
      <StyledBaseButton>
        <BaseButton type="button" onClick={handleNext}>Next</BaseButton>
      </StyledBaseButton>
    </Container>
  );
};

export default SignUpPasswordStep;

const Container = styled("div")({
  marginTop: "2rem",
  display: "flex",
  flexDirection: "column",
  gap: "1rem"
});

const Block = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  "& label": {
    fontSize: "12px",
    color: colors.textDark
  }
});

const FlexBlock = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  "& span": {
    fontSize: "10px",
    color: colors.textDark,
    opacity: 0.5
  }
});

const StyledBaseButton = styled("div")({
  marginTop: "16px",
  ".tablet &, .phone &": {
    position: "absolute",
    marginTop: 0,
    bottom: "0",
    left: "0",
    width: "100%",
    padding: "16px 16px 32px",
    background: "rgba(255, 255, 255, 0.9)",
    borderRadius: "24px"
  }
});
